import { useEffect, useState } from 'react';
import { supabase } from '../../supabase';
import { readAssurance } from '../../utils/auth/assuranceSettled';

/**
 * RD-SEC-051 — the shared "assurance settled" signal for one signed-in user.
 * `{ settled, aal }`: settled=false until the first check completes and while a
 * 2FA-enrolled user is still at aal1; re-evaluated on every auth event (aal1 →
 * aal2 arrives as one). The check is DEFERRED out of the onAuthStateChange
 * callback (auth-js deadlock rule) — no auth call runs inside it. The result is
 * stamped with the user id, so a previous user's answer is never returned.
 */
export default function useAssuranceSettled(sessionUserId) {
  const [state, setState] = useState({ userId: null, settled: false, aal: null });

  useEffect(() => {
    if (!sessionUserId) {
      // Sign-out: a same-user sign-back-in must never see the old session's answer.
      setState((prev) => (prev.userId === null && !prev.settled && prev.aal === null
        ? prev : { userId: null, settled: false, aal: null }));
      return undefined;
    }
    let cancelled = false;
    const evaluate = async () => {
      const next = await readAssurance(supabase);
      if (cancelled) return;
      setState((prev) => (
        prev.userId === sessionUserId && prev.settled === next.settled && prev.aal === next.aal
          ? prev
          : { userId: sessionUserId, settled: next.settled, aal: next.aal }
      ));
    };
    void evaluate();
    const sub = supabase.auth?.onAuthStateChange?.((event) => {
      if (event === 'SIGNED_OUT') return;
      setTimeout(() => { void evaluate(); }, 0);
    });
    return () => {
      cancelled = true;
      sub?.data?.subscription?.unsubscribe?.();
    };
  }, [sessionUserId]);

  if (!sessionUserId || state.userId !== sessionUserId) return { settled: false, aal: null };
  return { settled: state.settled, aal: state.aal };
}
